/**
 * Generated by orval v7.9.0 🍺
 * Do not edit manually.
 * Meeting BaaS API
 * API for managing meeting bots, calendar integrations, and webhooks
 * OpenAPI spec version: 2.0.0
 */

import type { BodyType } from "../../../../custom-axios"

import { customInstance } from "../../../../custom-axios"
import type {
  CreateMeetWorkspace201,
  CreateMeetWorkspaceBody,
  DeleteMeetWorkspace200,
  GetMeetWorkspace200,
  ListMeetWorkspaces200,
  ListMeetWorkspacesParams,
  UpdateMeetWorkspace200,
  UpdateMeetWorkspaceBody
} from "../../schema"

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1]

/**
 * Create a meet workspace — the parent resource that holds your SAML cert + private key for one Google Workspace's SSO config.

    A meet workspace represents one Google Workspace whose Legacy SSO profile points at our `/v2/meet-sso/*` endpoints. The cert and key on the workspace are shared by every `meet_login` (Workspace user identity) you attach to it, mirroring how Google Workspace stores a single verification certificate per SSO profile.

    **Two creation paths, same response shape:**

    - **Server-generated keypair** — pass `generate_keypair: true`. The server creates a self-signed RSA-2048 keypair with 10-year validity. Use this if you don't already have a SAML cert and want the simplest setup.
    - **Bring-your-own keypair** — pass `cert_pem` and `private_key_pem` together. Use this if you want to manage your own crypto or already have a cert/key pair you trust.

    Mutually exclusive: provide either `generate_keypair: true` OR (`cert_pem` + `private_key_pem`), not both.

    **Response always includes `cert_pem`** so you can upload it to Google's Legacy SSO profile in your Workspace admin console. `private_key_pem` is never returned.

    **After creation, you must:**
    1. Upload the returned `cert_pem` to Google Admin Console → Security → Set up SSO with third-party IdP → Legacy SSO profile.
    2. Set Sign-in URL to `https://api.meetingbaas.com/v2/meet-sso/sign-in` and Sign-out URL to `https://api.meetingbaas.com/v2/meet-sso/sign-out` in the same SSO profile. Enable "Use a domain-specific issuer" and assign the SSO profile to all users.
    3. Create one or more Workspace users that bots will sign in as, complete the "Welcome to Workspace" interactive login for each, and set language to "English (United States)".
    4. Add `meet_logins` (one per Workspace user) referencing this `workspace_id`.
    5. Optionally call `POST /v2/meet-workspaces/:workspace_id/verify` to run pre-flight checks.

    **Security:** The cert and key are encrypted at rest using AES-256-GCM. `private_key_pem` is never echoed in any response — including subsequent GETs. If you need it back, you must rotate via PATCH.

    **Per-team uniqueness:** Each `domain` may exist at most once per team. Attempting to create a duplicate returns 409.

    **Error Scenarios:**
    - `409 Conflict`: A workspace for this `domain` already exists.
    - `422 Unprocessable Entity`: Invalid cert/key (parse failure or modulus mismatch); both `generate_keypair` and `cert_pem` provided; neither provided.
 * @summary Create a meet workspace
 */
export const createMeetWorkspace = (
  createMeetWorkspaceBody: BodyType<CreateMeetWorkspaceBody>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<CreateMeetWorkspace201>(
    {
      url: "/v2/meet-workspaces",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: createMeetWorkspaceBody
    },
    options
  )
}
/**
 * List all meet workspaces for your team.

    Returns each workspace's metadata including `workspace_id`, `name`, `domain`, `state`, the `cert_pem` (so you can re-upload to Google admin if needed), and any recent failure context. `private_key_pem` is never included.

    **State Field:**
    - `active`: workspace is healthy; bots assigned to logins under this workspace will sign in successfully.
    - `invalid`: the system auto-disabled the workspace because Google rejected our SAML assertion (typically a cert mismatch). Every login under an invalid workspace is blocked from being assigned. Re-upload the correct cert to Google admin and use PATCH to set `state` back to `active`.

    Use this endpoint to inventory your SAML SSO setup or build a dashboard listing workspaces by health.
 * @summary List meet workspaces
 */
export const listMeetWorkspaces = (
  params?: ListMeetWorkspacesParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ListMeetWorkspaces200>(
    { url: "/v2/meet-workspaces", method: "GET", params },
    options
  )
}
/**
 * Get full details for a single meet workspace.

    Returns the same fields as the list endpoint, scoped to one workspace. Useful for confirming the cert that was uploaded to Google matches the one we have on file (compare `cert_pem` against the Verification Certificate in your Google Admin Console).

    **Failure Context:** If `state` is `invalid`, the response includes `last_error_message`, `last_error_at`, and a structured `failure_data` object describing the bot run that triggered the auto-disable. Use this to investigate before re-enabling.

    Returns 404 if the workspace is not found or does not belong to your team.
 * @summary Get a meet workspace
 */
export const getMeetWorkspace = (
  workspaceId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetMeetWorkspace200>(
    { url: `/v2/meet-workspaces/${workspaceId}`, method: "GET" },
    options
  )
}
/**
 * Update a meet workspace — rename it, rotate its keypair, or re-enable it after auto-disable.

    **Rename:** pass only `name`.

    **Rotate cert/key:** pass both `cert_pem` and `private_key_pem` together. The new pair takes effect immediately for all logins under this workspace. You must upload the new cert to Google Admin Console at the same time — there is a brief window where the cert in our system and the cert in Google admin can diverge, during which bot sign-ins fail. Coordinate the swap.

    **Re-enable after auto-disable:** pass `state: "active"`. Only valid if the workspace is currently `invalid`. This clears `failure_data`, `last_error_message`, and `last_error_at` atomically. Only do this after fixing the underlying issue (e.g., re-uploading the correct cert to Google admin).

    `domain` is immutable. To change the domain, delete the workspace (after draining its logins) and create a new one.

    **State Restrictions:** PATCH cannot set `state` to `invalid` — that's system-only. Returns 400 if you try.

    **Error Scenarios:**
    - `400 Bad Request`: Attempted to set `state` to a value other than `active`.
    - `404 Not Found`: Workspace not found or does not belong to your team.
    - `422 Unprocessable Entity`: Cert/key parse failure or mismatched pair; `cert_pem` provided without `private_key_pem` (or vice versa).
 * @summary Update a meet workspace
 */
export const updateMeetWorkspace = (
  workspaceId: string,
  updateMeetWorkspaceBody: BodyType<UpdateMeetWorkspaceBody>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<UpdateMeetWorkspace200>(
    {
      url: `/v2/meet-workspaces/${workspaceId}`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: updateMeetWorkspaceBody
    },
    options
  )
}
/**
 * Delete a meet workspace.

    **This cascades to every `meet_login` under the workspace.** All login rows are removed from the database in the same transaction; any bots currently assigned to those logins will have their `assigned_meet_login_id` set to `null` (the row deletion does not stop in-flight bots, but their post-bot accounting will reference a now-deleted login).

    **In-Use Guard:** If any login under this workspace has an active SSO session in flight (`active_session_count > 0`), the request returns 409 instead of deleting. Wait for the bots to finish or stop them first via the bots API. The aggregate count across all child logins is shown in the error message.

    **Operational Note:** This is a hard delete. Any reference to deleted login `credential_id` values from old bot records becomes a dangling reference. The cascade is irreversible — re-creating a workspace gives you a new `workspace_id` even if you reuse the same domain.

    Returns 404 if the workspace is not found or does not belong to your team.
 * @summary Delete a meet workspace (cascades to its logins)
 */
export const deleteMeetWorkspace = (
  workspaceId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<DeleteMeetWorkspace200>(
    { url: `/v2/meet-workspaces/${workspaceId}`, method: "DELETE" },
    options
  )
}
export type CreateMeetWorkspaceResult = NonNullable<Awaited<ReturnType<typeof createMeetWorkspace>>>
export type ListMeetWorkspacesResult = NonNullable<Awaited<ReturnType<typeof listMeetWorkspaces>>>
export type GetMeetWorkspaceResult = NonNullable<Awaited<ReturnType<typeof getMeetWorkspace>>>
export type UpdateMeetWorkspaceResult = NonNullable<Awaited<ReturnType<typeof updateMeetWorkspace>>>
export type DeleteMeetWorkspaceResult = NonNullable<Awaited<ReturnType<typeof deleteMeetWorkspace>>>
